import { courseService } from '@/services/course.service';
import { certificationService } from '@/services/certification.service';
import { learningPathApiService } from '@/services/api/learning-path-api.service';
import { userService } from '@/services/user.service';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Award,
  BookOpen, Building,
  FolderTree,
  GraduationCap,
  LayoutGrid,
  Loader2,
  MapPin,
  Search,
  UserCheck,
  Users,
  X
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { menuItems } from './Sidebar';
import {
  PERMISSION_LIST_KEY,
  COURSE_CATALOG_PERMISSION,
  CERTIFICATION_CATALOG_PERMISSION,
  LEARNING_PATH_CATALOG_PERMISSION,
  EMPLOYEE_PROGRESS_PERMISSION,
  DEPARTMENT_PROGRESS_PERMISSION,
} from '@/constants/constant-permissions';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PageResult {
  title: string;
  path: string;
  icon: React.ReactNode;
  category: string;
  description?: string;
}


type TabType = 'pages' | 'courses' | 'certifications' | 'learning_paths' | 'employees' | 'departments';

export default function SearchModal({ isOpen, onClose }: SearchModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('pages');
  const [searchQuery, setSearchQuery] = useState('');
  // Debounced query drives the SERVER-side API tabs so we fire one request after the
  // user pauses (~350ms) instead of one per keystroke. The input still shows searchQuery.
  const debouncedQuery = useDebounce(searchQuery, 350);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  // Permission checks — tabs are hidden if user lacks write permission
  const permissions = useMemo(() => {
    const p: string[] = JSON.parse(sessionStorage.getItem(PERMISSION_LIST_KEY) || '[]');
    return {
      canAccessCourses: p.includes(COURSE_CATALOG_PERMISSION.COURSE_CATALOG_WRITE),
      canAccessCertifications: p.includes(CERTIFICATION_CATALOG_PERMISSION.CERTIFICATION_CATALOG_WRITE),
      canAccessLearningPaths: p.includes(LEARNING_PATH_CATALOG_PERMISSION.ADMIN_LEARNING_PATH_CATALOG_WRITE),
      canAccessEmployees: p.includes(EMPLOYEE_PROGRESS_PERMISSION.ADMIN_EMPLOYEE_PROGRESS_READ) || p.includes(EMPLOYEE_PROGRESS_PERMISSION.ADMIN_EMPLOYEE_PROGRESS_WRITE),
      canAccessDepartments: p.includes(DEPARTMENT_PROGRESS_PERMISSION.ADMIN_DEPARTMENT_PROGRESS_READ) || p.includes(DEPARTMENT_PROGRESS_PERMISSION.ADMIN_DEPARTMENT_PROGRESS_WRITE),
    };
  }, [isOpen]);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
      setActiveTab('pages');
      // Focus input after modal animation
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Build page search results from sidebar menu
  const buildPageResults = (query: string): PageResult[] => {
    if (query.length < 2) return [];

    const results: PageResult[] = [];
    const searchTerm = query.toLowerCase();

    menuItems.forEach(item => {
      // Search main menu items
      if (item.title.toLowerCase().includes(searchTerm)) {
        results.push({
          title: item.title,
          path: item.children ? item.children[0]?.path || item.path : item.path,
          icon: item.icon,
          category: 'Main Menu',
        });
      }

      // Search sub-menu items
      if (item.children) {
        item.children.forEach(child => {
          if (child.title.toLowerCase().includes(searchTerm)) {
            results.push({
              title: child.title,
              path: child.path,
              icon: child.icon,
              category: item.title,
              description: `Found in ${item.title}`,
            });
          }
        });
      }
    });

    return results.slice(0, 8);
  };

  // Fetch courses
  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ['search-courses', debouncedQuery],
    queryFn: () => courseService.getCourses({
      search: debouncedQuery,
      limit: 8,
      page: 1
    }),
    enabled: activeTab === 'courses' && debouncedQuery.length >= 2,
    staleTime: 30 * 1000,
  });

  // Fetch certifications
  const { data: certificationsData, isLoading: certificationsLoading } = useQuery({
    queryKey: ['search-certifications', debouncedQuery],
    queryFn: () => certificationService.getMinimalCertifications({
      search: debouncedQuery,
      limit: 8,
      page: 1,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
    }),
    enabled: activeTab === 'certifications' && debouncedQuery.length >= 2,
    staleTime: 30 * 1000,
  });

  // Fetch learning paths
  const { data: learningPathsData, isLoading: learningPathsLoading } = useQuery({
    queryKey: ['search-learning-paths', debouncedQuery],
    queryFn: () => learningPathApiService.listLearningPaths({
      search: debouncedQuery,
      limit: 8,
      skip: 0,
    }),
    enabled: activeTab === 'learning_paths' && debouncedQuery.length >= 2,
    staleTime: 30 * 1000,
  });

  // Fetch employees
  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ['search-employees', debouncedQuery],
    queryFn: () => userService.getUsers({
      search: debouncedQuery,
      pageSize: 8,
      page: 1
    }),
    enabled: activeTab === 'employees' && debouncedQuery.length >= 2,
    staleTime: 30 * 1000,
  });

  // Fetch departments — lightweight, SERVER-side name search (dropdown endpoint,
  // no analytics) instead of the heavy /groups/list. Fast + still filtered on the DB.
  const { data: departmentsData, isLoading: departmentsLoading } = useQuery({
    queryKey: ['search-departments', debouncedQuery],
    queryFn: () => userService.searchDepartments(debouncedQuery, 8),
    enabled: activeTab === 'departments' && debouncedQuery.length >= 2,
    staleTime: 30 * 1000,
  });

  // Get current results based on active tab
  const getCurrentResults = () => {
    switch (activeTab) {
      case 'pages':
        return buildPageResults(searchQuery);
      case 'courses':
        return coursesData?.data || [];
      case 'certifications':
        return certificationsData?.certifications || [];
      case 'learning_paths':
        return learningPathsData?.items || [];
      case 'employees':
        // userService.getUsers() already transforms the response and returns users in 'data'
        return employeesData?.data || [];
      case 'departments':
        return departmentsData?.data || [];
      default:
        return [];
    }
  };

  const getCurrentLoading = () => {
    switch (activeTab) {
      case 'pages':
        return false;
      case 'courses':
        return coursesLoading;
      case 'certifications':
        return certificationsLoading;
      case 'learning_paths':
        return learningPathsLoading;
      case 'employees':
        return employeesLoading;
      case 'departments':
        return departmentsLoading;
      default:
        return false;
    }
  };

  const results = getCurrentResults();
  const isLoading = getCurrentLoading();

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }

    if (results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % results.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
        break;
      case 'Enter':
        e.preventDefault();
        handleResultClick(results[selectedIndex]);
        break;
      case 'Tab': {
        e.preventDefault();
        const tabIds = tabs.map(t => t.id);
        const currentIndex = tabIds.indexOf(activeTab);
        const nextIndex = e.shiftKey
          ? (currentIndex - 1 + tabIds.length) % tabIds.length
          : (currentIndex + 1) % tabIds.length;
        setActiveTab(tabIds[nextIndex]);
        setSelectedIndex(0);
        break;
      }
    }
  };

  // Handle result click based on type
  const handleResultClick = (result: any) => {
    switch (activeTab) {
      case 'pages':
        if (result.path?.startsWith('http')) {
          window.open(result.path, '_blank', 'noopener,noreferrer');
        } else {
          navigate(result.path);
        }
        break;
      case 'courses':
        navigate(`/admin/edit-course/${result.id}`);
        break;
      case 'certifications':
        navigate(`/admin/certifications/${result.certification_id}`);
        break;
      case 'learning_paths':
        navigate(`/admin/learning-paths/${result._id}`);
        break;
      case 'employees':
        navigate(`/admin/users/${result._id}/courses`);
        break;
      case 'departments':
        navigate(`/admin/groups/${result.group_key}/courses`);
        break;
    }
    onClose();
  };

  const allTabs = [
    { id: 'pages' as TabType, label: 'Pages', icon: <LayoutGrid className="w-4 h-4" />, visible: true },
    { id: 'courses' as TabType, label: 'Courses', icon: <GraduationCap className="w-4 h-4" />, visible: permissions.canAccessCourses },
    { id: 'certifications' as TabType, label: 'Certifications', icon: <Award className="w-4 h-4" />, visible: permissions.canAccessCertifications },
    { id: 'learning_paths' as TabType, label: 'Learning Paths', icon: <MapPin className="w-4 h-4" />, visible: permissions.canAccessLearningPaths },
    { id: 'employees' as TabType, label: 'Employees', icon: <UserCheck className="w-4 h-4" />, visible: permissions.canAccessEmployees },
    { id: 'departments' as TabType, label: 'Departments', icon: <FolderTree className="w-4 h-4" />, visible: permissions.canAccessDepartments },
  ];
  const tabs = allTabs.filter(t => t.visible);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen pt-12 px-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Search className="w-6 h-6 text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900">Search</h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Search Input */}
          <div className="p-6 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                placeholder={`Search ${allTabs.find(t => t.id === activeTab)?.label?.toLowerCase() || ''}...`}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedIndex(0);
                }}
                onKeyDown={handleKeyDown}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-lg"
              />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex overflow-x-auto border-b border-gray-200 bg-gray-50 scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSelectedIndex(0);
                  searchInputRef.current?.focus();
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-3 text-xs font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-primary-700 bg-white border-b-2 border-primary-500'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="max-h-96 overflow-y-auto">
            {searchQuery.length < 2 ? (
              <div className="p-8 text-center text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">Start typing to search</p>
                <p className="text-sm">Search for {activeTab} by typing at least 2 characters</p>
                <div className="mt-4 text-xs text-gray-400">
                  <p>Use Tab/Shift+Tab to switch between categories</p>
                  <p>Use ↑↓ to navigate results, Enter to select</p>
                </div>
              </div>
            ) : isLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-primary-600" />
                <p className="text-gray-600">Searching {activeTab}...</p>
              </div>
            ) : results.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium mb-2">No results found</p>
                <p className="text-sm">No {activeTab} found for "{searchQuery}"</p>
              </div>
            ) : (
              <div className="p-4">
                {results.map((result: any, index: number) => (
                  <button
                    key={index}
                    onClick={() => handleResultClick(result)}
                    className={`w-full flex items-center gap-4 p-4 rounded-lg text-left transition-colors ${
                      index === selectedIndex
                        ? 'bg-primary-50 border border-primary-200'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {activeTab === 'pages' ? (
                        result.icon
                      ) : activeTab === 'courses' ? (
                        <BookOpen className="w-5 h-5 text-blue-600" />
                      ) : activeTab === 'certifications' ? (
                        <Award className="w-5 h-5 text-amber-600" />
                      ) : activeTab === 'learning_paths' ? (
                        <MapPin className="w-5 h-5 text-emerald-600" />
                      ) : activeTab === 'employees' ? (
                        <Users className="w-5 h-5 text-green-600" />
                      ) : (
                        <Building className="w-5 h-5 text-purple-600" />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 truncate">
                        {activeTab === 'pages' ? result.title :
                         activeTab === 'courses' ? result.title :
                         activeTab === 'certifications' ? result.title :
                         activeTab === 'learning_paths' ? result.title :
                         activeTab === 'employees' ? result.full_name :
                         result.group_name}
                      </div>

                      <div className="text-sm text-gray-500 truncate">
                        {activeTab === 'pages' ? (result.description || result.category) :
                         activeTab === 'courses' ? (result.description || `Category: ${result.category || 'General'}`) :
                         activeTab === 'certifications' ? (result.description || `${result.question_count || 0} questions • ${result.time_limit_minutes || 0} min`) :
                         activeTab === 'learning_paths' ? (result.description || `${result.total_courses || 0} courses`) :
                         activeTab === 'employees' ? result.email :
                         result.description || `${result.user_count || 0} members`}
                      </div>
                      
                      {activeTab === 'employees' && result.groups?.length > 0 && (
                        <div className="text-xs text-gray-400 mt-1">
                          Department: {result.groups[0].group_name}
                        </div>
                      )}
                      
                      {activeTab === 'employees' && (
                        <div className="text-xs text-gray-400 mt-1">
                          {result.employee_id && `ID: ${result.employee_id} • `}
                          {result.courses.assigned} courses assigned
                        </div>
                      )}
                    </div>
                    
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}